import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateUtils } from "utils/dateutils";
import { QUERY_AUDIT_CHANGE_EVENT } from "./gql-queries";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  isSessionTerminating,
  onSessionTerminating
} from "services/session-state";
import { ErrorTypes } from "../constants";

export type TAuditChangeLog = {
  created_at: string;
  change_type: string;
  data_source: string;
  info: {
    refreshList: Array<any>;
    submittal_ids?: Array<string>;
    material_ids?: Array<string>;
    computation?: boolean;
    event_type?: string;
    status?: string;
    import_type?: string;

    feature_type?: string;
    feature_instance_ids?: Array<string>;
    feature_type_ids?: Array<number>;
    linked_features?: Array<{ id: string; featureType: string }>;
  };
  object_id: null | string;
  project_id: null | string;
  subscription_id: null | string;
  new_mode: boolean;
};

export enum InfoFeatureType {
  submittal = "SUBMITTAL",
  material = "MATERIALS",
  bidPackage = "BID_PACKAGE",
  designPackage = "DESIGN_PACKAGE",
  planningActivity = "PLANNING_ONLY_ACTIVITY",
  planningMaterial = "PLANNING_ONLY_MATERIAL",
  planningSubmittal = "PLANNING_ONLY_SUBMITTAL"
}

const POLLING_INTERVAL = 1000;

function useChangeEventPolling({
  gqlClient,
  projectId,
  subscriptionId
}: {
  gqlClient: any;
  projectId?: string;
  subscriptionId?: string;
}) {
  const [lastFetchDate, setLastFetchDate] = useState(
    DateUtils.dateTimeObj().utc().toISOString()
  );

  const where = useMemo(() => {
    return {
      created_at: {
        _gt: lastFetchDate
      },
      ...(projectId ? { project_id: { _eq: projectId } } : {}),
      ...(subscriptionId ? { subscription_id: { _eq: subscriptionId } } : {})
    };
  }, [lastFetchDate, projectId, subscriptionId]);

  // Polling is controlled through the pollInterval OPTION, never the
  // imperative start/stopPolling pair: useQuery re-applies its options on
  // every render, which silently restarts a stopped poll — a halt only
  // sticks when the option itself computes to 0.
  const [halts, setHalts] = useState(() => ({
    // Hard browser offline only (onLine === false is trustworthy). When
    // onLine is true but nothing routes (VPN/WSL adapters), the poll must
    // KEEP running: its failures/successes are the heartbeat that drives
    // connectivity-state's derived-offline detection and recovery.
    offline: typeof navigator !== "undefined" && navigator.onLine === false,
    // A schema/permission validation error is permanent for this session's
    // role — Hasura rejects every retry identically.
    permanentlyRejected: false,
    // Teardown must silence the poll so it doesn't fire (and 401) during
    // the logout round-trip + redirect window.
    terminating: isSessionTerminating(),
    debug: false
  }));
  const haltsRef = useRef(halts);
  haltsRef.current = halts;
  const setHalt = useCallback((key: keyof typeof halts, value: boolean) => {
    setHalts((current) =>
      current[key] === value ? current : { ...current, [key]: value }
    );
  }, []);
  const pollingHalted =
    halts.offline ||
    halts.permanentlyRejected ||
    halts.terminating ||
    halts.debug;

  const { data, error, refetch } = useCIQQuery<{
    audit_change_events: Array<TAuditChangeLog>;
  }>(QUERY_AUDIT_CHANGE_EVENT, {
    fetchPolicy: "network-only",
    skip: !gqlClient,
    client: gqlClient,
    pollInterval: pollingHalted ? 0 : POLLING_INTERVAL,
    variables: {
      where: {
        ...where
      }
    }
  });

  const eventLogs = useMemo(
    () => data?.audit_change_events ?? [],
    [data?.audit_change_events]
  );

  useEffect(() => {
    if (eventLogs?.length) {
      setLastFetchDate(eventLogs[0].created_at);
    }
  }, [eventLogs]);

  useEffect(() => {
    const permanentlyRejected = error?.graphQLErrors?.some(
      (e: any) =>
        e?.extensions?.code === "validation-failed" ||
        ErrorTypes.fieldNotFound(e?.message || "")
    );
    if (permanentlyRejected) {
      setHalt("permanentlyRejected", true);
    }
  }, [error, setHalt]);

  useEffect(() => {
    const onOffline = () => setHalt("offline", true);
    const onOnline = () => {
      if (haltsRef.current.permanentlyRejected || isSessionTerminating()) {
        return;
      }
      setHalt("offline", false);
      // Catch up immediately instead of waiting for the next tick.
      refetch()?.catch?.(() => {});
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, [refetch, setHalt]);

  useEffect(() => {
    // Global toggles for debugging
    (window as any).stopAuditLogPolling = () => setHalt("debug", true);
    (window as any).startAuditLogPolling = () => setHalt("debug", false);

    const unsubscribeTerminating = onSessionTerminating(() =>
      setHalt("terminating", true)
    );

    return () => {
      unsubscribeTerminating();
    };
  }, [setHalt]);

  return { data: eventLogs, refetch };
}

export default useChangeEventPolling;
